import { useCallback, useEffect, useRef, useState } from 'react';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export interface PermissionSnapshot { permissions: Record<string, boolean>; features: Record<string, boolean>; isSuperAdmin?: boolean; }
const PERMISSIONS_CACHE_TTL_MS = 60_000;
const cache = new Map<string, { snapshot: PermissionSnapshot; fetchedAt: number }>();
// Cache-fix (2026-09-29, Jaime-approved): dedupe in-flight identical requests —
// every component mounting in the same tick with the same condo key shares one
// promise instead of firing N parallel GETs (the refetch-on-mount storm).
const inFlight = new Map<string, Promise<PermissionSnapshot>>();

function fetchPermissionsSnapshot(key: string, activeCondoId?: string): Promise<PermissionSnapshot> {
  const existing = inFlight.get(key);
  if (existing) return existing;
  const apply = (r: any): PermissionSnapshot => {
    const d = r.data?.data || r.data || {};
    return { permissions: d.permissions || {}, features: d.features || {}, isSuperAdmin: !!d.isSuperAdmin };
  };
  const p = authAPI.getPermissions(activeCondoId)
    .then(apply)
    .catch(() => authAPI.getPermissions().then(apply))
    .finally(() => { inFlight.delete(key); });
  inFlight.set(key, p);
  return p;
}
const DOMAIN_BY_MODULE: Record<string, string> = { dashboard:'dashboard', announcements:'announcement', anuncios:'announcement', voting:'vote', votaciones:'vote', amenities:'amenity', amenidades:'amenity', calendar:'calendar', calendario:'calendar', visitors:'visitor', visitantes:'visitor', expenses:'finance', gastos:'finance', financial:'finance', finanzas:'finance', payments:'finance', morosos:'moroso', workOrders:'maintenance', mantenimiento:'maintenance', complaints:'complaint', quejas:'complaint', emergency:'condo', qr:'access', verify:'access', access:'access', panic:'panic', home:'home', supplier:'supplier', suppliers:'supplier', marketplace:'supplier', user:'user', residents:'user', condo:'condo', communication:'communication' };
export const domainForModule = (module: string) => DOMAIN_BY_MODULE[module] || module;
// (2026-09-05 fail-closed) Only a FRESH cached snapshot may be rendered without
// loading; an expired entry is treated as absent so gated items never flash
// before the current context's permissions land.
const isFresh = (entry?: { fetchedAt: number }) => !!entry && Date.now() - entry.fetchedAt < PERMISSIONS_CACHE_TTL_MS;
const EMPTY_SNAPSHOT: PermissionSnapshot = { permissions:{}, features:{} };

// OFFLINE MENU CACHE (2026-10-31, Jaime directive): every successful snapshot
// is ALSO persisted per context key (`vecindus_permissions:<condoId|__global__>`).
// When a context that was never active while online is activated OFFLINE, the
// in-memory cache is empty and the fetch cannot succeed — previously this
// degraded to the fail-closed EMPTY snapshot and collapsed the menu (TH1 in
// the live defect lost its home modules). The persisted snapshot now resolves
// the menu from the last-known truth instead, and background revalidation
// replaces it as soon as connectivity returns. clearAuth()'s defensive sweep
// of `vecindus_permissions:*` keys keeps this per-account (no cross-account
// leakage on logout).
const PERMISSIONS_STORE_PREFIX = 'vecindus_permissions:';
// Long-horizon bound (nit-a, kimi re-gate review 2026-10-31: 7d -> 72h for
// permission-bearing data): a persisted snapshot is only used as the offline/
// initial render source within 72 hours of its fetch; past that it is treated
// as absent (fail-closed as before). Permissions change too often to trust a
// week-old menu snapshot.
const PERMISSIONS_STORE_TTL_MS = 72 * 60 * 60 * 1000;
interface StoredPermissionsSnapshot { snapshot: PermissionSnapshot; fetchedAt: number; }
export function readStoredPermissionsSnapshot(key: string): StoredPermissionsSnapshot | undefined {
  try {
    const raw = localStorage.getItem(PERMISSIONS_STORE_PREFIX + key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as StoredPermissionsSnapshot;
    if (!parsed || typeof parsed !== 'object' || !parsed.snapshot) return undefined;
    return parsed;
  } catch { return undefined; }
}
const isStoredUsable = (entry?: StoredPermissionsSnapshot) => !!entry && Date.now() - entry.fetchedAt < PERMISSIONS_STORE_TTL_MS;
const writeStoredPermissionsSnapshot = (key: string, snapshot: PermissionSnapshot) => {
  try { localStorage.setItem(PERMISSIONS_STORE_PREFIX + key, JSON.stringify({ snapshot, fetchedAt: Date.now() } as StoredPermissionsSnapshot)); } catch { /* storage optional */ }
};

export function usePermissions() {
  const { activeCondoId, user } = useAuth(); const key = activeCondoId || '__global__';
  const [snapshot, setSnapshot] = useState<PermissionSnapshot>(() => {
    if (isFresh(cache.get(key))) return cache.get(key)!.snapshot;
    const stored = readStoredPermissionsSnapshot(key);
    if (isStoredUsable(stored)) return stored!.snapshot;
    return EMPTY_SNAPSHOT;
  });
  const [loading, setLoading] = useState(() => {
    if (!user) return false;
    if (isFresh(cache.get(key))) return false;
    if (isStoredUsable(readStoredPermissionsSnapshot(key))) return false;
    return true;
  });
  const [cacheTick, setCacheTick] = useState(0);
  const prevKeyRef = useRef(key);
  useEffect(() => { const timer = window.setInterval(() => setCacheTick((tick) => tick + 1), PERMISSIONS_CACHE_TTL_MS); return () => window.clearInterval(timer); }, []);
  useEffect(() => { let cancelled=false; if (!user) { setLoading(false); prevKeyRef.current = key; return; } const cached=cache.get(key);
    const contextSwitched = prevKeyRef.current !== key;
    prevKeyRef.current = key;
    if (isFresh(cached)) { setSnapshot(cached!.snapshot); setLoading(false); return; }
    const stored = readStoredPermissionsSnapshot(key);
    if (isStoredUsable(stored)) {
      // Offline-tolerant context switch (2026-10-31): render THIS context's
      // last-known snapshot immediately (no skeleton, no fail-closed blank)
      // and revalidate in the background. Never renders ANOTHER context's
      // snapshot — the stored key IS the new context's key.
      setSnapshot(stored!.snapshot);
      setLoading(false);
    } else if (contextSwitched) {
      // (2026-09-05) A condo/unit context switch invalidates the 60s cache
      // IMMEDIATELY — never render the previous context's expired snapshot.
      // Fail closed: drop to an empty snapshot with loading=true (skeleton,
      // no gated items) until the fresh response arrives.
      if (cached) cache.delete(key);
      setSnapshot(EMPTY_SNAPSHOT);
      setLoading(true);
    } else if (!cached) {
      setLoading(true);
    }
    // Same-key TTL revalidation (cacheTick) keeps stale-while-revalidate: the
    // rendered snapshot stays in place while refreshing so the menu never
    // blanks once a minute. Context switches are handled above (fail-closed).
    if (!cached) setLoading(true);
    const applyResponse = (n: PermissionSnapshot) => {
      cache.set(key, { snapshot: n, fetchedAt: Date.now() });
      writeStoredPermissionsSnapshot(key, n);
      if (cancelled) return;
      // Skip the state update when the payload is unchanged so `can`/`hasFeature`
      // identities stay stable and downstream effect deps don't re-fire.
      setSnapshot((current) => (JSON.stringify(current) === JSON.stringify(n) ? current : n));
    };
    fetchPermissionsSnapshot(key, activeCondoId)
      .then(applyResponse)
      .catch(() => {
        if (!cancelled) {
          // Do not cache a failure as a valid all-empty snapshot: that cached
          // empty state collapsed the menu to 1-2 items (Allegra regression,
          // 2026-08-26). Keep whatever snapshot we had and leave the cache
          // entry absent so the next tick/context change retries. Never clear
          // the rendered snapshot on error.
          const prev = cache.get(key)?.snapshot;
          const fallback = prev || (isStoredUsable(stored) ? stored!.snapshot : undefined);
          if (fallback) setSnapshot(fallback);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return()=>{cancelled=true;}; },[activeCondoId,key,user,cacheTick]);
  // Fail-closed gates (2026-09-05): while permissions are loading, `can`/
  // `hasFeature` DENY. The old `loading ||` form granted everything during
  // load, producing the wrong-menu flash this hook was known for.
  const can=useCallback((d:string)=>!loading&&(!!snapshot.isSuperAdmin||snapshot.permissions[d]===true),[loading,snapshot]);
  const hasFeature=useCallback((f:string)=>!loading&&(!!snapshot.isSuperAdmin||snapshot.features[f]===true),[loading,snapshot]);
  return {...snapshot,loading,can,hasFeature};
}
export const clearPermissionsCache=()=>cache.clear();
export const invalidatePermissionsCache = clearPermissionsCache;
export default usePermissions;
